<template>
  <div
    v-if="fullyLoaded"
    class="pa-3 flex"
  >
    <div class="title mb-3">
      Payment management
    </div>
    <div v-if="!feesAreOk">
      <div class="alert">
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span>Please fill in the fees in the parameters of POP before using this
          module.</span>
      </div>
    </div>
    <div v-else>
      <section
        id="filters"
        class="mb-3"
      >
        <v-select
          v-model="filters.application.value"
          v-focus
          :items="applications"
          hide-details
          single-line
          class="mt-0 pt-0 ml-4 select-mode"
          :disabled="filters.application.disabled"
          @input="onApplicationChange(false)"
        />
        <v-select
          v-if="filters.team.displayed"
          v-model="filters.team.value"
          v-focus
          :items="teams"
          hide-details
          single-line
          class="mt-0 pt-0 ml-4 select-mode"
          :disabled="filters.team.disabled"
          item-text="name"
          item-value="id"
          @input="onTeamChange"
        />
      </section>
      <section
        id="to-manage"
        class="mt-4"
      >
        <div class="sub-filters-wrapper">
          <div class="sub-filters">
            <v-select
              v-model="filters.indemnities_status"
              label="Status of the indemnities form"
              solo
              single-line
              hide-details
              :items="status"
              item-text="name"
              item-value="value"
              class="w300p not-flex"
              @change="onListFiltersChange"
            />
            <v-autocomplete
              v-model="filters.user_id"
              label="Trainer / AS"
              :items="trainers"
              item-value="id"
              :filter="customFilter"
              clearable
              @click:clear="filters.user_id = null"
              @change="onListFiltersChange"
            >
              <template #item="{ item }">
                {{ item.firstname }} {{ item.lastname }}
              </template>
              <template #selection="{ item }">
                {{ item.firstname }} {{ item.lastname }}
              </template>
            </v-autocomplete>
            <a @click="resetSubFilters">Reset filters</a>
          </div>
          <div id="global-actions">
            <v-btn
              color="primary"
              :disabled="
                filters.indemnities_status != Consts.INDEMNITIES_STATUS.DRAFT
              "
              @click="publishInBatch()"
            >
              Publish in batch
            </v-btn>
            <a @click="goToBatches">Go to the list of batches</a>
          </div>
        </div>
        <div id="list">
          <indemnities-list
            v-if="fullyLoaded"
            ref="indemnitiesList"
            :application="filters.application.value"
            :team="filters.team.value"
            :indemnities-status="filters.indemnities_status"
            :user-id="filters.user_id"
          />
        </div>
      </section>
    </div>
    <v-dialog
      v-model="independentsModal"
      width="750"
    >
      <v-card>
        <v-card-title class="title">
          Invoice numbers for independents
        </v-card-title>
        <v-card-text>
          <p>
            Some of the indemnities you're trying to publish relate to
            independents. You need to enter their invoice number in order to
            proceed
          </p>
          <div>
            <table class="inside-table">
              <tr
                v-for="i in independents"
                :key="`ind-${i.id}`"
              >
                <td>
                  {{ i.user.firstname }} <strong>{{ i.user.lastname }}</strong>
                </td>
                <td>
                  from {{ formatDate(i._first_session_date) }} to
                  {{ formatDate(i._last_session_date) }}
                </td>
                <td>{{ numberFormat(i.total_cost) }} €</td>
                <td>
                  <v-text-field
                    v-model="invoicesNumbers[i.id]"
                    :hide-details="true"
                    label="Invoice n°"
                  />
                </td>
              </tr>
            </table>
          </div>
        </v-card-text>
        <v-card-actions
          layout="row"
          layout-align="end center"
        >
          <v-btn
            depressed
            color="error"
            @click="independentsModal = false"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="success"
            @click="publishInBatch(true)"
          >
            Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Auth from "@/plugins/auth";
import Consts from "@/consts";
import GlobalMixin from "@/mixins/global";
import Params from "@/api/params";
import IndemnitiesList from "./Indemnities.vue";
import Indemnities from "@/api/indemnities";
import DateMixin from "@/mixins/date";

const formater = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2, // Force au moins 2 décimales
  maximumFractionDigits: 2, // Limite à 2 décimales
});

export default {
  components: { IndemnitiesList },
  mixins: [GlobalMixin, DateMixin],
  data() {
    return {
      filters: {
        application: {
          value: Consts.APPLICATION.ALL,
          disabled: false,
        },
        team: {
          value: 0, //Toutes
          displayed: true,
          disabled: false,
        },
        indemnities_status: Consts.INDEMNITIES_STATUS.DRAFT,
        user_id: null,
      },
      status: [
        { value: null, name: "All" },
        { value: Consts.INDEMNITIES_STATUS.DRAFT, name: "Waiting" },
        {
          value: Consts.INDEMNITIES_STATUS.IN_PROGRESS,
          name: "Beeing treated",
        },
        {
          value: Consts.INDEMNITIES_STATUS.PUBLISHED,
          name: "Already published",
        },
      ],
      fullyLoaded: false,
      trainers: [],
      independentsModal: false,
      independents: [],
      invoicesNumbers: {},
      feesAreOk: false,
      Consts,
      teams: [],
      allTeams: [],
    };
  },
  async mounted() {
    const { data: teams } = await Params.getList("teams", {
      is_enabled: 1,
      sortBy: "name",
    });
    this.allTeams = teams;
    this.allTeams.unshift({ id: 0, name: "All" });

    const { data: checkFees } = await Indemnities.checkFees();
    this.feesAreOk = !!checkFees.ok;
    //on doit récupérer des infos sur l'utilisateur pour connaître la nature des filtres auxquels il aura accès
    this.filters.application.value = Auth.user.application;
    this.filters.application.disabled =
      !Auth.user.is_admin && this.filters.application.value > 0;

    //on va tout de même checker ce qu'on a dans le store
    const app = this.getStoredApp();
    if (Auth.user.application == Consts.APPLICATION.ALL && app) {
      this.filters.application.value = app;
    }
    const storedFilters = this.getStoredObject("indemnities.sub_filters");
    if (storedFilters) {
      this.filters = Object.assign(this.filters, storedFilters);
    }

    this.onApplicationChange(true);
    this.fullyLoaded = true;
  },
  methods: {
    async loadTrainers() {
      const { data } = await Params.getList("users", {
        application: this.filters.application.value,
        sortBy: "firstname",
        role: Consts.TRAINER
      });
      this.trainers = data;
    },
    onApplicationChange(fromMounted = false) {
      if (this.filters.application.value == Consts.APPLICATION.FREELANCES) {
        if(Auth.user.is_admin){
          this.teams = this.allTeams
        } else {
          this.teams = Auth.user.teams
        }

        this.filters.team.displayed = true;
        const storedTeam = this.getStoredTeam();
        if (Auth.user.teams && Auth.user.teams.length) {
          this.filters.team.value =
            storedTeam && storedTeam != 0
              ? storedTeam
              : Auth.user.teams[0].id;
          if (!Auth.user.is_admin && Auth.user.teams.length == 1) {
            this.filters.team.disabled = true; // en gros on l'affiche mais on le disable parce que le mec n'est pas admin donc il ne peut voir que ça
          }
        } else {
          this.filters.team.value =
            storedTeam && storedTeam != 0 ? storedTeam : 0;
        }
      } else {
        this.filters.team.displayed = false;
        this.filters.team.value = 0;
        this.teams = this.allTeams
      }
      this.setStoredApp(this.filters.application.value);
      this.setStoredTeam(this.filters.team.value);
      if (!fromMounted) {
        this.filters.user_id = null;
        this.onListFiltersChange();
      }
      this.loadTrainers();
    },
    onTeamChange() {
      this.setStoredTeam(this.filters.team.value);
      this.onListFiltersChange();
    },
    onListFiltersChange() {
      // eslint-disable-next-line no-unused-vars
      const { application, team, ...subfilters } = this.filters;
      this.setStoredObject("indemnities.sub_filters", subfilters);
      this.refreshIndemnities(true);
    },
    resetSubFilters() {
      this.filters.indemnities_status = Consts.INDEMNITIES_STATUS.DRAFT;
      this.filters.user_id = null;
      this.onListFiltersChange();
    },
    /**
     * Au départ j'utilisais un :key avec la dernière date de filtrage
     * pour forcer l'actualisation des sessions
     * mais ça faisait plein de merdes, en particulier des double-appels à l'API
     * Solution : appeler le refresh du fils par le parent
     * le nextTick est important, sinon le fils n'a pas le temps de s'actualiser au niveau des props
     */
    refreshIndemnities(filtered = false) {
      this.$nextTick(() => {
        this.$refs.indemnitiesList &&
          this.$refs.indemnitiesList.fetch(filtered ? "filters" : "local");
      });
    },
    async publishInBatch(force = false) {
      //on doit d'abord voir si on n'a pas des indépendants dans la sélection
      const { data: independentsInside } = await Indemnities.independentsInside(
        {
          application: this.filters.application.value,
          team_id: this.filters.team.value,
          user_id: this.filters.user_id,
        }
      );

      if (
        independentsInside.length == 0 ||
        (force &&
          independentsInside.length == Object.keys(this.invoicesNumbers).length)
      ) {
        const { data: back } = await Indemnities.publishInBatch({
          application: this.filters.application.value,
          team_id: this.filters.team.value,
          user_id: this.filters.user_id,
          status: this.filters.indemnities_status,
          invoices_numbers: this.invoicesNumbers,
        });
        if (back.total > 1) {
          this.$snotify.success(
            `The batch has been created with success. ${back.total} indemnities records are waiting to be processed`
          );
        } else {
          this.$snotify.success(
            `The batch has been created with success. ${back.total} indemnity record is waiting to be processed`
          );
        }
        this.resetIndependents();
        this.refreshIndemnities(true);
      } else if (!force) {
        //on doit afficher une modale qui permettra de recueillir pour chaque indépendant le n° de facture
        this.independents = independentsInside;
        this.invoicesNumbers = {};
        this.independentsModal = true;
      } else {
        this.$snotify.error(
          `You need to fill in all invoice numbers before proceeding to the creation of the batch`
        );
      }
    },
    resetIndependents() {
      this.invoicesNumbers = {};
      this.independentsModal = false;
    },
    customFilter(item, queryText) {
      // Combine firstname + lastname pour la recherche
      const fullName = `${item.firstname} ${item.lastname}`.toLowerCase();
      const searchText = queryText.toLowerCase();
      return fullName.includes(searchText);
    },
    numberFormat(number) {
      return formater.format(number);
    },
    goToBatches() {
      this.$router.push({ name: "payments-payments-batches" });
    },
  },
};
</script>
<style scoped lang="sass">
#filters
  display: flex
  flex-direction: row
  align-items: center
  justify-content: start

  .v-input
    width: 250px
    flex: 0 0 auto !important

#stats
  display: flex
  flex-direction: row
  justify-content: space-around
  flex-wrap: wrap

  .stat
    width: 25%
    min-width: 250px
    display: flex
    flex-direction: column
    background-color: white
    padding: 10px
    margin-top: 10px

    .figure
      font-weight: bold
      color: #f4b944
      font-size: 64px
      text-align: center

    .subtitle
      text-align: center
      color: #333333
      font-size: 1.1rem

    .sub-footer
      display: flex
      flex-direction: row
      justify-content: flex-end

      &.centered
        justify-content: center

      .alert
        margin-right: 5px
        width: 40px
        height: 40px
        font-size: 40px !important
        color: tomato

      .badge
        background-color: tomato
        color: white
        font-weight: bold
        display: flex
        font-size: 0.85rem
        justify-content: center
        align-items: center
        padding: 1px 6px
        border-radius: 25px
        align-self: flex-start
.sub-filters-wrapper
  width: 100%
  display: flex
  flex-direction: row
  justify-content: space-between

#global-actions
  display: flex
  flex-direction: center
  align-items: center

  a
    text-decoration: underline

.sub-filters
  padding: 10px
  display: flex
  flex-direction: row
  justify-content: flex-start
  align-items: center
  flex-wrap: wrap

  > *
    width: 350px
    margin-right: 10px

    &.date
      width: 200px
    &.switch
      width: 270px
  a
    text-decoration: underline

.w300p
  width: 300px
.not-flex
  flex: none

.inside-table
  border-collapse: collapse
  width: 100%

  tr:nth-child(event)
    background-color: #FAFAFA
  td
    border: 1px solid #F1F1F1
    padding: 10px 5px

.alert
  padding: 2em
  border-radius: 9px
  border: 2px solid tomato
  color: tomato
  width: 100%
  font-size: 1.2rem
  display: flex
  flex-direction: row
  align-items: center

  i.v-icon
    font-size: 60px
    color: tomato
    margin-right: 20px
</style>
